// client/src/session/index.ts — the only session API the app is allowed to import · UI-003,
// filled by AUTH-004.
//
// Everything the shell knows about the signed-in user comes through here. The SOURCE behind
// it was `./stub.ts` (a two-organization fixture) and is now `./source.ts` — the token and
// claims reader over `POST /auth/*` and `GET /users/me`. That swap touched no component, which
// is why no component may reach past this barrel.
//
// Shape note: the session is exposed through the Zustand auth store (04 §9.5), not a context
// provider, because 04 §9.1's composition root is NORMATIVE and lists no provider. A store
// also gives the router what a context cannot: `getSession()` and `ensureSession()` are
// callable from `beforeLoad`, which runs outside React (§9.2.2).
//
// There is no organization switch here any more (D-1542). A session reads from every
// organization the user belongs to at once, so the header switcher, the chooser after login
// and the switch-before-open of the properties list all went with `useSwitchOrganization`.
// The `organizations` list on the session is information — the profile shows it — not a
// choice.

import { type AuthStatus, useAuthStore } from '@/stores/auth-store';

import type { Permission } from './catalog';
import type { Session } from './types';

export type { AuthStatus } from '@/stores/auth-store';
export type { Permission, RoleCode } from './catalog';
export type { MfaMethod, PendingMfa, Session, SessionOrganization } from './types';
export {
  connectHttp,
  ensureSession,
  establishSession,
  logoutHard,
  refreshSession,
  reloadSession,
  signOut,
} from './source';

/**
 * The session, outside React — route guards and loaders read it here (§9.2.2). Null while
 * anonymous. A guard that needs the bootstrap to have run awaits `ensureSession()` instead.
 */
export function getSession(): Session | null {
  return useAuthStore.getState().session;
}

/**
 * What the signed-in person signs in with: the address, or — for an account a landlord created
 * for a tenant with none (D-2710) — the user name. Every place that names who is signed in
 * shows this rather than `email`, which is null for that account (D-2715). One of the two is
 * always set, so the empty string is a shape the server never produces rather than a state.
 */
export function loginOf(session: Pick<Session, 'email' | 'userName'>): string {
  return session.email ?? session.userName ?? '';
}

/**
 * `loginOf` for whoever is signed in right now, outside React — or null while anonymous. The
 * two pages that tell a visitor „you are signed in as …" after an address mismatch read it at
 * the moment of the refusal, before „ieși din cont" leaves no session to read.
 */
export function signedInLogin(): string | null {
  const session = getSession();
  return session === null ? null : loginOf(session);
}

/**
 * The session, inside React. Re-renders every consumer when it is replaced.
 *
 * Non-null by contract: every consumer sits under the `/app` guard, which redirected before
 * this could render without one. Throwing here rather than returning null keeps the shell's
 * components free of a `session?.` that could never legitimately be taken.
 */
export function useSession(): Session {
  const session = useAuthStore((state) => state.session);
  if (!session) {
    throw new Error(
      'useSession() rendered outside an authenticated shell — the /app guard (04 §9.2.2) must run first.',
    );
  }
  return session;
}

/**
 * Whether a session is held at this moment, inside React — the one question a SHELL asks before
 * any of its components calls `useSession()` (D-4041).
 *
 * The guard runs before a shell mounts, so a shell never STARTS without a session; it can still
 * outlive one. „Deconectare" and a refused refresh both clear the store first and navigate to
 * `/login` after, and between the two the store re-renders the mounted shell with no session:
 * every `useSession()` in it throws, and the route's error boundary painted „A apărut o eroare"
 * over the page for that moment. The three layouts (`/app`, `/portal`, `/admin`) ask this and
 * render nothing instead, so the page goes blank for the instant before the login page arrives.
 */
export function useHasSession(): boolean {
  return useAuthStore((state) => state.session !== null);
}

/**
 * Whether the bootstrap has run, and what it found. The one screen that needs this rather than
 * <see cref="useSession"/> is the invitation page (SCR-AUTH-006): it sits under `_auth`, so it
 * renders for an anonymous visitor as well, and what it shows is decided by exactly this
 * (D-1590). Everything under `/app` is past the guard and asks for the session itself.
 */
export function useSessionStatus(): AuthStatus {
  return useAuthStore((state) => state.status);
}

/**
 * The client-side mirror of a permission (§6.2). UX ONLY: it decides whether a nav item is
 * rendered, never whether data may be read — the API is the enforcement (02 §6.4). A user who
 * reaches a permission-gated URL directly gets SCR-SYS-403, not a blank page.
 *
 * The set is the default organization's — the one that gates every request. Since D-1540 a
 * permitted read reaches every organization the user belongs to in which the permission also
 * holds; that widening needs no mirror here.
 */
export function useCan(permission: Permission): boolean {
  const session = useSession();
  return session.permissions.includes(permission);
}

/**
 * Whether the signed-in person is waiting for their first quota: they hold one in no property
 * at all, and they belong to a portfolio besides the one their own signup created.
 *
 * Since BR-COOWN-07 a member reads a property only while holding a current quota in
 * it, so an invitee's first screens are legitimately empty — and „you have no properties yet,
 * add one" is then the wrong sentence and the wrong offer. The two conditions are both needed:
 * a landlord whose portfolio is genuinely empty is the first-property case and still gets it,
 * and the count alone cannot tell the two apart (D-1625).
 *
 * More than one PORTFOLIO is the reading of „a member elsewhere": signup creates exactly one
 * organization for the person (D-1530), so a second portfolio membership is somebody else's. A
 * clădire's own organization is not counted (D-3343) — a resident of a block is a member of it,
 * and is not thereby somebody's co-owner waiting for a quota; counting it would show a new owner
 * the „waiting" state on an empty portfolio instead of the offer to add their first property.
 */
export function useAwaitingFirstQuota(): boolean {
  const session = useSession();
  const portfolios = session.organizations.filter((organization) => organization.kind === 'PORTFOLIO');
  return session.ownedPropertyCount === 0 && portfolios.length > 1;
}

/**
 * Whether this account is a delegated one that has not chosen a client yet, or has no client
 * to choose (D-1699).
 *
 * `'choose'` is the ordinary state a staff member starts every session in: they hold clients
 * and nothing picks one for them, which is the product owner's fail-closed rule (D-1698).
 * `'none'` is the one an owner leaves behind when they revoke the last delegation — the
 * account is still a member, and there is nothing for it to do until somebody invites it
 * again. `null` is everybody else, including a staff account that HAS chosen.
 *
 * It is read where `useAwaitingFirstQuota` is read, and answered first: both describe an empty
 * page, and „you hold no quota yet" is the wrong sentence for somebody who simply has not
 * said whose portfolio they are opening.
 */
export function useDelegationPrompt(): 'choose' | 'none' | null {
  const session = useSession();

  if (session.activeClientId !== null) {
    return null;
  }

  if (session.clients.length > 0) {
    return 'choose';
  }

  // No clients AND a staff role: an account that was delegated and no longer is. An owner
  // reaches neither branch, because the role test fails and the array is empty anyway.
  return session.role === 'manager' || session.role === 'accountant' ? 'none' : null;
}

/**
 * Whether this account may read the tenant portal: it is a leasing party on at least one
 * contract, whatever role its own organization gives it.
 *
 * NOT `role === 'tenant'`. `role` describes the membership in `activeOrganizationId`, and being
 * somebody's tenant is not a role — a landlord who has accepted a portal invitation from another
 * landlord holds `owner` in their own organization and is a party to a lease in somebody else's.
 * The server agrees by the same rule: `Portal.Self` is held by `owner` too since D-1720, and what
 * narrows the surface is `PortalScopeResolver`, which keys on the caller's leasing parties rather
 * than on their role (D-1727).
 *
 * A `tenant`-role account with no lease is therefore not a portal READER: the portal it would
 * reach answers empty lists and 404s. It is still let through the route, because `/app` has
 * even less for it and bounces it back — the loop D-2010 closes. The route's own comment
 * carries that exception; this predicate stays the plain question it has always been.
 *
 * The one place that still reads the role is the session's own default landing, which is a
 * different question.
 */
export function isPortalTenant(session: Pick<Session, 'portalContractCount'>): boolean {
  return session.portalContractCount > 0;
}
